import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'

/**
 * Klient HTTP do komunikacji z backendem Go.
 * - Automatycznie dodaje nagłówek Authorization z tokenem JWT
 * - Automatycznie odświeża token gdy wygaśnie (401)
 * - Obsługuje kolejkowanie żądań podczas odświeżania tokenu
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api'

/* Klucze localStorage */
const ACCESS_STORAGE_SLOT = import.meta.env.VITE_ACCESS_STORAGE_SLOT || 'nhex_at'
export const SESSION_EXPIRED_EVENT = 'nhex:session-expired'

let sessionExpiryNotified = false
let refreshPromise: Promise<string> | null = null

/* Instancja axios */
const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 15_000,
  withCredentials: true,
})

/* ==================== ZARZĄDZANIE TOKENAMI ==================== */

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_STORAGE_SLOT)
}

export function setTokens(access: string): void {
  localStorage.setItem(ACCESS_STORAGE_SLOT, access)
  sessionExpiryNotified = false
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_STORAGE_SLOT)
}

function accessTokenExpiresSoon(token: string, skewSeconds = 30): boolean {
  try {
    const encodedPayload = token.split('.')[1]
    if (!encodedPayload) return true
    const normalized = encodedPayload.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='))) as {
      exp?: unknown
      token_type?: unknown
    }
    if (payload.token_type !== 'access' || typeof payload.exp !== 'number') return true
    return payload.exp <= Math.floor(Date.now() / 1000) + skewSeconds
  } catch {
    return true
  }
}

async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const { data } = await axios.post<{ access_token: string }>(`${API_BASE}/auth/refresh`, null, {
        withCredentials: true,
      })
      if (!data?.access_token) throw new Error('Refresh response did not contain an access token')
      setTokens(data.access_token)
      return data.access_token
    } catch (error) {
      clearTokens()
      notifySessionExpired()
      throw error
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

function notifySessionExpired(): void {
  if (sessionExpiryNotified) return
  sessionExpiryNotified = true
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
}

/* ==================== INTERCEPTORY ==================== */

/* Dodaj token do każdego żądania */
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  let token = getAccessToken()
  // Refresh before protected polling requests leave the browser. This avoids a
  // visible 401 every time a short-lived access token reaches its boundary.
  if (token && accessTokenExpiresSoon(token)) {
    token = await refreshAccessToken()
  }
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/* Automatyczne odświeżanie tokenu przy 401 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    /* Jeśli 401 i mamy refresh token — spróbuj odświeżyć */
    if (
      error.response?.status === 401 &&
      getAccessToken() &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true
      try {
        const accessToken = await refreshAccessToken()
        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        return api(originalRequest)
      } catch (refreshError) {
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  },
)

/* ==================== TYPY ODPOWIEDZI ==================== */

/** Standardowa odpowiedź API z paginacją */
export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  per_page: number
}

/** Standardowa odpowiedź błędu */
export interface ApiError {
  error: string
  details?: Record<string, string[]>
}

export default api
